import { useMutation, useQuery } from "@apollo/client";
import { useState } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BackIcon, ComputerIcon, PhoneIcon } from "src/components/Icons";
import LuxeFeaturesModal from "src/components/LuxeFeaturesModal";
import PaymentFlowModal from "src/components/PaymentFlowModal";
import StyledButton from "src/components/StyledButton.tsx";
import { SelectTemplate } from "src/graphql/mutations";
import { EventBySlug, GetEvent, GetLuxeDetails } from "src/graphql/queries";
import { getRefreshToken } from "src/helper/storage";
import Iframe from "src/pages/website/Templates/Iframe";
import useAppStore from "src/store/utils";
import { DescribeLuxe, event } from "src/types/api";

type OrientationType = "portrait" | "landscape";

const TemplateDemo = () => {
  const [orientation, setOrientation] = useState<OrientationType>("portrait");
  const { event, setEvent } = useAppStore((state) => state);
  const { id } = useParams();

  const templateIdArray = id?.split("-");
  const template_id = templateIdArray
    ?.slice(0, templateIdArray.length - 1)
    .join("-");

  const navigate = useNavigate();

  const [currency, setCurrency] = useState<string | undefined>("");
  const [discountedCost, setDiscountedCost] = useState<number | undefined>(0);
  const [undiscountedCost, setUnDiscountedCost] = useState<number | undefined>(
    0
  );

  const [isLuxeFeaturesOpen, setIsLuxeFeaturesOpen] = useState(false);

  const refreshToken = getRefreshToken();

  const [openPaymentFlow, setOpenPaymentFlow] = useState(false);

  useQuery<DescribeLuxe>(GetLuxeDetails, {
    variables: { event_id: event?.uuid },
    fetchPolicy: "network-only",
    // fetchPolicy: "cache-and-network",
    onCompleted(response) {
      setCurrency(response.describeLuxe.currency);
      setDiscountedCost(response.describeLuxe.discounted_amount);
      setUnDiscountedCost(response.describeLuxe.undiscounted_amount);
    },
    onError(error) {
      if (error?.message !== "unauthenticated") {
        toast.error(
          <p className="toast">{error?.message ?? "An error occured"}</p>
        );
      } else {
        navigate("/clear");
      }
    },
  });

  // const isLuxeEvent = useContext(AppContext)?.isLuxeEvent;

  const [isLuxeEvent, setIsLuxeEvent] = useState(false);

  const [selectTemplate, { loading }] = useMutation(SelectTemplate, {
    onCompleted() {
      toast.success(<p className="toast">Template selected</p>);
      setEvent({ ...event, template_id });
    },
    onError(error) {
      if (error?.message !== "unauthenticated") {
        toast.error(
          <p className="toast">{error?.message ?? "An error occured"}</p>
        );
      } else {
        navigate("/clear");
      }
    },
  });

  const openLuxeFeaturesModal = () => {
    if (!isLuxeEvent) {
      setIsLuxeFeaturesOpen(true);
    } else {
      selectTemplate({
        variables: {
          req: {
            event_id: event?.uuid,
            template_id,
          },
        },
        refetchQueries: [
          {
            query: EventBySlug,
            variables: {
              slug: event?.slug,
            },
          },
        ],
      });
    }
  };

  useQuery<event>(GetEvent, {
    variables: {
      eventId: event?.id,
    },
    onCompleted(data) {
      setIsLuxeEvent(data.event?.luxe ?? false);
    },
  });

  const previewSlugObject = {
    default: "theominisparty",
    // eterna: "bridegroom-2025",
    lumen: "groombride-2025",
    lilac: "thenotebook-2025",
    blanc: "gracewedsgiovani-2025",
    funky: "nonsohitchesfrancesca-2025",
    french: "lafayettealisson-2025",
    fairy: "alexanderwedselizabeth-2025",
  };

  // const previewSlug =
  //   template_id === "default"
  //     ? "theominisparty"
  //     : // : template_id === "eterna"
  //       //   ? "bridegroom-2025"
  //       template_id === "lumen"
  //       ? "groombride-2025"
  //       : template_id === "lilac"
  //         ? "thenotebook-2025"
  //         : event.slug;

  return (
    <div className="flex h-[100vh] flex-col p-6">
      <header className="mb-8 flex w-full justify-between">
        <div className="flex items-center gap-2">
          <Link
            to={"/website/templates"}
            className="flex items-center gap-2 p-2 transition duration-500 ease-in-out hover:border-b-2 hover:border-[black] hover:font-semibold hover:text-primary-5"
          >
            <span className="">
              <BackIcon />
            </span>
            <nav className="uppercase">templates</nav>
          </Link>
        </div>
        <div className="flex items-center gap-4 text-sm uppercase">
          <StyledButton
            // label="view-demo"
            // design="primary"
            variant="border-black"
            className="hidden w-40 rounded border py-2 lg:block"
          >
            <a
              target="_blank"
              href={`https://${template_id ? previewSlugObject[template_id as keyof typeof previewSlugObject] : ""}.inawo.dev/?jelfo=og5xovf7i8f6ic678h_hk_8nl&evd=${refreshToken}&ppptf=ew2n498hdae3dd2h3a0gh24`}
              rel="noreferrer"
              // className={styles["dashboardLayout__preview"]}
            >
              View Demo Site
            </a>
          </StyledButton>

          {event.template_id !== template_id && (
            <StyledButton
              // label="view-demo"
              // design="secondary"
              variant="border-black"
              className="hidden w-40 rounded border bg-[black] py-2 text-white lg:block"
              onClick={openLuxeFeaturesModal}
            >
              {loading ? `Using...` : `Use this template`}
            </StyledButton>
          )}
        </div>
      </header>
      <LuxeFeaturesModal
        isLuxeFeaturesOpen={isLuxeFeaturesOpen}
        opened={isLuxeFeaturesOpen}
        setIsLuxeFeaturesOpen={setIsLuxeFeaturesOpen}
        currency={currency}
        discountedCost={discountedCost}
        undiscountedCost={undiscountedCost}
        setOpenPaymentFlow={setOpenPaymentFlow}
      />
      <PaymentFlowModal
        openPaymentFlow={openPaymentFlow}
        setOpenPaymentFlow={setOpenPaymentFlow}
      />

      <div className="flex items-center justify-center gap-6">
        <div
          className={`${orientation === "portrait" ? `border border-[black]` : ``} cursor-pointer rounded-md border p-1`}
          onClick={() => setOrientation("portrait")}
        >
          <span className="">
            <PhoneIcon />
          </span>
        </div>
        <div
          className={`${orientation === "landscape" ? `border border-[black]` : ``} cursor-pointer rounded-md border p-1`}
          onClick={() => setOrientation("landscape")}
        >
          <ComputerIcon />
        </div>
      </div>

      <main className="flex flex-grow flex-col items-center justify-center">
        <Iframe
          orientation={orientation}
          src={`https://${template_id ? previewSlugObject[template_id as keyof typeof previewSlugObject] : ""}.inawo.dev/`}
        />

        <div className="flex flex-col gap-8 text-sm sm:flex-row lg:hidden">
          <StyledButton
            variant="border-black"
            className="w-40 rounded border py-2"
          >
            <a
              target="_blank"
              href={`https://${template_id ? previewSlugObject[template_id as keyof typeof previewSlugObject] : ""}.inawo.dev/?jelfo=og5xovf7i8f6ic678h_hk_8nl&evd=${refreshToken}&ppptf=ew2n498hdae3dd2h3a0gh24`}
              rel="noreferrer"
              // className={styles["dashboardLayout__preview"]}
            >
              View Demo Site
            </a>
          </StyledButton>
          {event.template_id !== template_id && (
            <StyledButton
              variant="border-black"
              className="w-40 rounded border bg-[black] py-2 text-white"
              onClick={openLuxeFeaturesModal}
            >
              {loading ? `Using...` : `Use this template`}
            </StyledButton>
          )}
        </div>
      </main>
    </div>
  );
};

export default TemplateDemo;
